import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from '../components/common';
import { COMPANY_SETTINGS, S } from '../utils/constants';
import { authenticateUser } from '../services/userService';
import { AppContext } from '../context/AppContext';

/**
 * Login Page
 * Handles user authentication with remember me functionality
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Get login from context
  const { login: contextLogin } = useContext(AppContext) || {};

  // Toast helper
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Load saved credentials on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('gosnel_remember_user');
    const savedPass = localStorage.getItem('gosnel_remember_pass');
    if (savedUser && savedPass) {
      setUsername(savedUser);
      setPassword(savedPass);
      setRememberMe(true);
    }
    
    // Don't auto-redirect from here - let user login manually
    // This prevents navigation throttling issues
  }, []);

  const handleLogin = async () => {
    if (!username || !password) {
      setError(true);
      return;
    }

    setLoading(true);
    setError(false);

    try {
      const user = await authenticateUser(username, password);

      if (user) {
        // Save credentials if remember me is checked
        if (rememberMe) {
          localStorage.setItem('gosnel_remember_user', username);
          localStorage.setItem('gosnel_remember_pass', password);
        } else {
          localStorage.removeItem('gosnel_remember_user');
          localStorage.removeItem('gosnel_remember_pass');
        }

        // Use context login to set currentUser in AppContext
        if (contextLogin) {
          contextLogin(user);
        }
        
        // Small delay to ensure context is updated before navigation
        setTimeout(() => {
          // Redirect unapproved users to learning, approved users to dashboard
          const redirectPage = user.is_approved_to_work ? 'dashboard' : 'learning';
          navigate(`/${user.role}/${redirectPage}`, { replace: true });
        }, 100);
      } else {
        setError(true);
        showToast('بيانات الدخول غير صحيحة', 'error');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(true);
      showToast('خطأ في تسجيل الدخول', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: S.bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      direction: "rtl",
      fontFamily: "'Cairo',sans-serif",
      padding: "20px"
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;900&display=swap" rel="stylesheet"/>
      
      <div style={{
        width: "100%",
        maxWidth: 440,
        background: S.card,
        borderRadius: 24,
        padding: "clamp(24px, 5vw, 40px)",
        border: `1px solid ${S.border}`,
        boxShadow: "0 40px 80px rgba(0,0,0,0.8)"
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <img 
            src={COMPANY_SETTINGS.companyLogo} 
            alt="GoSnel Logo" 
            style={{
              height: 64,
              margin: "0 auto 14px",
              objectFit: "contain",
              borderRadius: "50%"
            }}
            onError={e => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <div style={{
            width: 64,
            height: 64,
            margin: "0 auto 14px",
            borderRadius: 18,
            background: "linear-gradient(135deg,#4ecdc4,#f97316)",
            display: "none",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 30
          }}>
            🎯
          </div>
          <h1 style={{ color: S.txt, fontSize: 22, fontWeight: 900, marginBottom: 4 }}>
            GoSnel CRM
          </h1>
          <p style={{ color: "#475569", fontSize: 13 }}>
            نظام إدارة دورة المطاعم
          </p>
        </div>

        {/* Form */}
        <Input
          label="اسم المستخدم"
          value={username}
          onChange={e => { setUsername(e.target.value); setError(false); }}
          placeholder="username"
          onKeyDown={e => e.key === "Enter" && handleLogin()}
          disabled={loading}
          autoComplete="off"
        />

        <Input
          label="كلمة المرور"
          type="password"
          value={password}
          onChange={e => { setPassword(e.target.value); setError(false); }}
          placeholder="••••••"
          onKeyDown={e => e.key === "Enter" && handleLogin()}
          disabled={loading}
          style={{ marginTop: 14 }}
        />

        {error && (
          <p style={{ color: "#f87171", fontSize: 13, textAlign: "center", marginTop: 10 }}>
            بيانات الدخول غير صحيحة
          </p>
        )}
        
        {/* Remember Me */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 14,
          marginBottom: 6
        }}>
          <input 
            type="checkbox" 
            id="rememberMe"
            checked={rememberMe}
            onChange={e => setRememberMe(e.target.checked)}
            style={{
              width: 16,
              height: 16,
              cursor: "pointer",
              accentColor: "#4ecdc4"
            }}
          />
          <label 
            htmlFor="rememberMe" 
            style={{
              color: S.muted,
              fontSize: 13,
              cursor: "pointer",
              userSelect: "none"
            }}
          >
            تذكرني
          </label>
        </div>
        
        {/* Login Button */}
        <Button 
          onClick={handleLogin} 
          disabled={loading}
          style={{
            width: "100%",
            justifyContent: "center",
            fontSize: 15,
            padding: "12px 0",
            marginTop: 20,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'جاري الدخول...' : 'تسجيل الدخول'}
        </Button>

        {/* Info Box */}
        <div style={{
          marginTop: 22,
          background: "#ffffff",
          borderRadius: 14,
          padding: 16,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}>
          <p style={{ color: "#0f172a", fontSize: 11, fontWeight: 700, marginBottom: 10 }}>
            ℹ️ معلومات الدخول
          </p>
          <p style={{ color: "#64748b", fontSize: 12, lineHeight: 1.8, textAlign: "center" }}>
            استخدم بيانات الدخول الخاصة بك
            <br />
            <span style={{ color: "#4ecdc4", fontSize: 11 }}>
              Contact admin if you need access
            </span>
          </p>
        </div>
      </div>
      
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          background: toast.type === 'error' ? '#dc2626' : '#059669',
          color: '#fff',
          padding: '12px 24px',
          borderRadius: 12,
          fontFamily: "'Cairo',sans-serif",
          fontSize: 14,
          fontWeight: 700,
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          animation: 'slideIn 0.3s ease',
          zIndex: 9999
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
